/* eslint-disable */
// useAttachmentSearch — one query per family, server-side, limited.
//
// ⚠️ WHY NOT THE GLOBAL SEARCH'S QUERY LAYER. src/components/globalSearch does
// cover these same four families, and it is the obvious thing to reuse — but it
// fetches EVERY row and filters in the client (gsItemsQueryFn pages the whole
// items table 1,000 at a time). Mounting that here would make opening the
// composer pull ~8,000 items before the first keystroke. It also could not
// express two things this surface needs: a per-family error state, and the
// cancellation of a superseded request. Both only exist when a family IS a
// request. So this is a deliberately NARROWER search — code and name only,
// server-side, eight per family — and the two are not the same tool.
//
// ⚠️ CANCEL SUPERSEDED REQUESTS. A slow answer for "CL" must never overwrite a
// fast answer for "CL23". Every run takes a sequence number and a late run
// whose number is no longer current throws its result away. This is the defect
// that makes a search feel haunted.
//
// ⚠️ ONE FAMILY FAILING MUST NOT BLANK THE LIST. Promise.allSettled, and a
// family that rejects renders its own "couldn't search" row while the other
// three render normally.

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { ATTACH_FAMILIES } from '../utils/attachmentRegistry';

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
const PER_FAMILY = 8;

// PostgREST `or` needs the pattern inline, so a query containing a comma or a
// parenthesis would break out of the filter grammar. Strip those rather than
// escape them — a code never contains them, and a search is not a place to be
// clever with quoting.
function safeTerm(q) {
  return String(q || '').replace(/[(),*]/g, ' ').trim();
}

async function searchFamily(fam, term) {
  let q = supabase.from(fam.table).select(fam.select);
  if (fam.activeOnly) q = fam.activeOnly(q);
  const like = `%${term}%`;
  q = q.or(`${fam.codeCol}.ilike.${like},${fam.nameCol}.ilike.${like}`).limit(PER_FAMILY);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map((row) => ({
    kind: fam.kind,
    id: String(row[fam.idCol]),
    code: fam.code(row),
    name: fam.name(row),
    detail: fam.detail(row),
    image: fam.image(row),
  }));
}

export function useAttachmentSearch(query, attached) {
  const [groups, setGroups] = useState([]);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  const attachedKeys = (attached || []).map((a) => a.kind + '|' + a.id).join(',');

  useEffect(() => {
    const term = safeTerm(query);
    if (term.length < MIN_CHARS) { setGroups([]); setBusy(false); return undefined; }

    const mine = ++seq.current;
    setBusy(true);
    const t = setTimeout(async () => {
      const settled = await Promise.allSettled(
        ATTACH_FAMILIES.map((f) => searchFamily(f, term)),
      );
      if (mine !== seq.current) return;              // a newer keystroke owns the field

      const taken = new Set(attachedKeys ? attachedKeys.split(',') : []);
      const next = [];
      settled.forEach((r, i) => {
        const fam = ATTACH_FAMILIES[i];
        if (r.status === 'rejected') {
          next.push({ kind: fam.kind, label: fam.group, failed: true, rows: [] });
          return;
        }
        const rows = r.value
          .filter((row) => !taken.has(row.kind + '|' + row.id))
          .sort((a, b) => String(a.code).localeCompare(String(b.code)));
        if (rows.length) next.push({ kind: fam.kind, label: fam.group, failed: false, rows });
      });
      setGroups(next);
      setBusy(false);
    }, DEBOUNCE_MS);

    return () => clearTimeout(t);
  }, [query, attachedKeys]);

  // Invalidate any in-flight run — used when the picker closes.
  const cancel = useCallback(() => { seq.current++; setBusy(false); }, []);

  return { groups, busy, cancel, minChars: MIN_CHARS };
}

export default useAttachmentSearch;
